import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Cluster, Parking, ParkingStatus } from '@parkupp/core'
import { ClusterService } from '../../../services/cluster.service'
import { ParkingService } from '../../../services/parking.service'
import { ToastrService } from 'ngx-toastr'
import { doc, Firestore } from '@angular/fire/firestore'

@Component({
    selector: 'app-admin-create-cluster',
    templateUrl: './admin-create-cluster.component.html',
    styleUrls: ['./admin-create-cluster.component.scss'],
})
export class AdminCreateClusterComponent implements OnInit {
    clusterForm: FormGroup
    cluster: Cluster | null = null
    clusterKey: string | null = null
    parkings: Parking[] = []
    selectedParkings: string[] = []
    loading = false
    selectedLogoFile: File | null = null
    isEditMode = false

    constructor(
        private formBuilder: FormBuilder,
        private clusterService: ClusterService,
        private parkingService: ParkingService,
        private toastr: ToastrService,
        private router: Router,
        private route: ActivatedRoute,
        private firestore: Firestore
    ) {
        this.clusterForm = this.formBuilder.group({
            title: ['', Validators.required],
            description: [''],
        })
    }

    async ngOnInit() {
        // Load all published and private parkings
        const publishedParkings = await this.parkingService.listPromise({ status: ParkingStatus.PUBLISHED })
        const privateParkings = await this.parkingService.listPromise({ status: ParkingStatus.PRIVATE })
        this.parkings = [...publishedParkings, ...privateParkings]

        // Check if we're in edit mode
        this.clusterKey = this.route.snapshot.paramMap.get('clusterKey')
        if (this.clusterKey) {
            this.isEditMode = true
            await this.loadCluster()
        }
    }

    async loadCluster() {
        if (!this.clusterKey) return

        try {
            this.cluster = await this.clusterService.getPromise(this.clusterKey)

            // Populate form
            this.clusterForm.patchValue({
                title: this.cluster.title,
                description: this.cluster.description || '',
            })

            // Set selected parkings
            this.selectedParkings = this.cluster.parkingRefs?.map((ref) => ref.id) || []
        } catch (error) {
            console.error('Error loading cluster:', error)
            this.toastr.error('Failed to load cluster')
        }
    }

    onLogoSelected(event: any) {
        const file = event.target.files[0]
        if (file) {
            // Validate file type
            const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml']
            if (!validTypes.includes(file.type)) {
                this.toastr.error('Please upload a valid image file (PNG, JPG, SVG)')
                return
            }

            // Validate file size (max 5MB)
            const maxSize = 5 * 1024 * 1024
            if (file.size > maxSize) {
                this.toastr.error('File size must be less than 5MB')
                return
            }

            this.selectedLogoFile = file
        }
    }

    async uploadLogo(clusterKey: string): Promise<string | null> {
        if (!this.selectedLogoFile) return null

        try {
            const url = await this.clusterService.uploadLogo(this.selectedLogoFile, clusterKey)
            return url
        } catch (error) {
            console.error('Error uploading logo:', error)
            throw error
        }
    }

    async saveCluster() {
        if (this.clusterForm.invalid) {
            this.toastr.error('Please fill in all required fields')
            return
        }

        if (this.selectedParkings.length === 0) {
            this.toastr.error('Please select at least one parking')
            return
        }

        this.loading = true

        try {
            const parkingRefs = this.selectedParkings.map((parkingId) => doc(this.firestore, `parkings/${parkingId}`))

            const clusterData: any = {
                title: this.clusterForm.value.title,
                description: this.clusterForm.value.description || null,
                parkingRefs: parkingRefs,
            }

            let clusterKey: string

            if (this.isEditMode && this.clusterKey) {
                // Update existing cluster
                clusterKey = this.clusterKey

                // Upload new logo if selected
                if (this.selectedLogoFile) {
                    const logoUrl = await this.uploadLogo(clusterKey)
                    if (logoUrl) {
                        clusterData.logoUrl = logoUrl
                    }
                }

                await this.clusterService.update(clusterKey, clusterData)
                this.toastr.success('Cluster updated successfully!')
            } else {
                // Create new cluster
                clusterKey = await this.clusterService.create(clusterData)

                // Upload logo if selected
                if (this.selectedLogoFile) {
                    const logoUrl = await this.uploadLogo(clusterKey)
                    if (logoUrl) {
                        await this.clusterService.update(clusterKey, { logoUrl })
                    }
                }

                this.toastr.success('Cluster created successfully!')
            }

            // Navigate to the public cluster view
            this.router.navigate(['/cluster', clusterKey])
        } catch (error) {
            console.error('Error saving cluster:', error)
            this.toastr.error('Failed to save cluster. Please try again.')
        } finally {
            this.loading = false
        }
    }

    removeLogo() {
        if (!this.cluster || !this.clusterKey) return

        if (confirm('Are you sure you want to remove the logo?')) {
            this.clusterService
                .update(this.clusterKey, { logoUrl: undefined })
                .then(() => {
                    if (this.cluster) {
                        this.cluster.logoUrl = undefined
                    }
                    this.toastr.success('Logo removed successfully')
                })
                .catch((error) => {
                    console.error('Error removing logo:', error)
                    this.toastr.error('Failed to remove logo')
                })
        }
    }

    cancel() {
        this.router.navigate(['/admin/clusters'])
    }
}
